import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { db } from '../firebase/config';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { formatTimestamp } from '../utils/dateUtils';
import { runScheduleCheck, getScheduleIntervalMs } from '../utils/scheduleCheck';
import { pickAssignedChallengeIds, shuffleQuestionsEnabled, shuffleChoicesEnabled, identityChoiceOrder } from '../utils/challengeOrder';
import './UserPage.css';

const UserPage = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [currentChallengeIndex, setCurrentChallengeIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [feedbackDetail, setFeedbackDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // eslint-disable-next-line no-unused-vars -- setter is used, value is not needed
  const [assignedChallenges, setAssignedChallenges] = useState([]);
  const [challengeStartTime, setChallengeStartTime] = useState(null); // Track when current challenge was loaded
  const previousActiveRoundIndexRef = useRef(null); // Track previous active round using ref
  const previousRoundDataRef = useRef(null); // Track previous round data to detect resets
  const [breakSecondsRemaining, setBreakSecondsRemaining] = useState(null); // 3, 2, 1 or null when not in break
  const breakStartTimeRef = useRef(null);
  const totalBreakTimeAddedRef = useRef(0); // Seconds to subtract from round elapsed (break time not counted)
  const inBreakRef = useRef(false); // True while break countdown is showing (so timer effect can pause main time)
  const breakParamsRef = useRef(null); // Params for advanceAfterBreak when break ends
  const lastActiveRoundIndexForBreakRef = useRef(null);
  const [breakLengthFromSettings, setBreakLengthFromSettings] = useState(3);
  const [roundStartInstructions, setRoundStartInstructions] = useState({});
  const [reviewRoundIndex, setReviewRoundIndex] = useState(null); // when set, show read-only review for that round
  // Shuffled display order for choices (array of original indices, e.g. [2,0,3,1]) so correct is not always first
  const [choiceOrder, setChoiceOrder] = useState([]);
  // Ref keeps display order in sync with current challenge (avoids any render using identity order)
  const choiceOrderForChallengeRef = useRef({ challengeId: null, order: [] });

  // Fisher-Yates shuffle of indices [0..n-1]
  const shuffleIndices = (n) => {
    const arr = Array.from({ length: n }, (_, i) => i);
    for (let i = n - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // Shuffled order that is never identity [0,1,...,n-1] when n > 1 (so choices are not shown in "correct" series)
  const shuffledChoiceOrder = (n) => {
    if (n <= 1) return n ? [0] : [];
    let order = shuffleIndices(n);
    const isIdentity = order.every((val, i) => val === i);
    if (isIdentity) {
      // swap first two so order is different from [0,1,...,n-1]
      [order[0], order[1]] = [order[1], order[0]];
    }
    return order;
  };

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const competitionId = localStorage.getItem('competitionId');

    if (!userId || !competitionId) {
      navigate('/');
      return;
    }

    loadData();
    const unsubscribe = setupRealtimeUpdates();
    const competitionUnsubscribe = setupCompetitionListener(competitionId);

    // Check competition status periodically
    const statusInterval = setInterval(() => {
      checkCompetitionStatus();
    }, 5000);

    // Auto open/close competition and rounds by schedule
    runScheduleCheck();
    const scheduleId = setInterval(runScheduleCheck, getScheduleIntervalMs());

    return () => {
      clearInterval(statusInterval);
      clearInterval(scheduleId);
      if (unsubscribe) unsubscribe();
      if (competitionUnsubscribe) competitionUnsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Subscribe to global settings for break length and round start instructions
  useEffect(() => {
    const settingsRef = doc(db, 'settings', 'global');
    const unsubscribe = onSnapshot(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        if (data.breakLengthSeconds !== undefined) {
          const val = Number(data.breakLengthSeconds);
          setBreakLengthFromSettings(isNaN(val) || val < 0 ? 3 : Math.min(120, val));
        } else {
          setBreakLengthFromSettings(3);
        }
        if (data.roundStartInstructions && typeof data.roundStartInstructions === 'object') {
          setRoundStartInstructions(data.roundStartInstructions);
        }
      } else {
        setBreakLengthFromSettings(3);
      }
    });
    return () => unsubscribe();
  }, []);

  // Timer effect - updates every second
  useEffect(() => {
    if (!competition || !userData) return;
    
    const timer = setInterval(() => {
      const activeRoundIndex = competition.rounds?.findIndex(r => r.isActive);
      if (activeRoundIndex >= 0) {
        const activeRound = competition.rounds[activeRoundIndex];
        const roundData = userData.roundData?.[activeRoundIndex] || {};
        
        // Restore persisted break offset when switching to a round (do not wipe on first tick)
        if (lastActiveRoundIndexForBreakRef.current !== activeRoundIndex) {
          totalBreakTimeAddedRef.current = Number(roundData.totalBreakSeconds) || 0;
          lastActiveRoundIndexForBreakRef.current = activeRoundIndex;
        }
        
        // Don't update timer if round is done
        if (roundData.isDone) {
          setTimeLeft(0);
          return;
        }
        
        if (roundData.startTime) {
          const startTime = roundData.startTime.toDate ? roundData.startTime.toDate() : new Date(roundData.startTime);
          const currentTime = new Date();
          const breakAdded = totalBreakTimeAddedRef.current || 0;
          // During break, subtract current break elapsed so the main round time appears paused
          const currentBreakElapsed = (inBreakRef.current && breakStartTimeRef.current)
            ? (Date.now() - breakStartTimeRef.current) / 1000
            : 0;
          const elapsed = (currentTime - startTime) / 1000 - breakAdded - currentBreakElapsed;
          const maxTime = activeRound.maxTime || 0;
          const remaining = Math.max(0, maxTime - elapsed);
          const remainingSeconds = Math.floor(remaining);
          setTimeLeft(remainingSeconds);
          
          // If time runs out, mark round as done
          if (remainingSeconds <= 0 && !roundData.isDone) {
            markRoundAsDone(activeRoundIndex);
          }
        } else {
          setTimeLeft(activeRound.maxTime || 0);
        }
      } else {
        setTimeLeft(0);
      }
    }, 1000);

    return () => clearInterval(timer);
  // markRoundAsDone intentionally omitted to avoid effect re-running every render
  }, [competition, userData]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkCompetitionStatus = async () => {
    const competitionId = localStorage.getItem('competitionId');
    if (!competitionId) return;

    try {
      const competitionDoc = await getDoc(doc(db, 'competitions', competitionId));
      if (competitionDoc.exists()) {
        const compData = competitionDoc.data();
        if (!compData.isActive) {
          localStorage.removeItem('userId');
          localStorage.removeItem('competitionId');
          localStorage.removeItem('userName');
          localStorage.removeItem('userDateOfBirth');
          navigate('/');
        }
      }
    } catch (err) {
      console.error('Error checking competition:', err);
    }
  };

  const loadData = async () => {
    try {
      const userId = localStorage.getItem('userId');
      const competitionId = localStorage.getItem('competitionId');

      // Load competition
      const competitionDoc = await getDoc(doc(db, 'competitions', competitionId));
      if (!competitionDoc.exists() || !competitionDoc.data().isActive) {
        navigate('/');
        return;
      }
      const compData = { id: competitionId, ...competitionDoc.data() };
      setCompetition(compData);

      // Load user
      const userDoc = await getDoc(doc(db, 'competitions', competitionId, 'users', userId));
      if (!userDoc.exists()) {
        navigate('/');
        return;
      }
      const user = { id: userId, ...userDoc.data() };
      setUserData(user);

      // Check if user is frozen
      if (user.frozen) {
        alert(t('accountFrozen'));
        navigate('/');
        return;
      }

      // Get currently active round
      const activeRoundIndex = compData.rounds?.findIndex(r => r.isActive);
      
      // Update previous active round index for tracking
      previousActiveRoundIndexRef.current = activeRoundIndex >= 0 ? activeRoundIndex : null;
      
      if (activeRoundIndex >= 0) {
        const activeRound = compData.rounds[activeRoundIndex];
        const roundData = user.roundData?.[activeRoundIndex] || {};
        totalBreakTimeAddedRef.current = Number(roundData.totalBreakSeconds) || 0;
        lastActiveRoundIndexForBreakRef.current = activeRoundIndex;

        // Store round data in ref for comparison in real-time listener
        if (!previousRoundDataRef.current) {
          previousRoundDataRef.current = {};
        }
        // Store a copy of round data to detect changes
        previousRoundDataRef.current[activeRoundIndex] = { ...roundData };
        
        // Check if round is already done - if so, show only the completion message
        if (roundData.isDone === true) {
          // Round is already completed - show message and don't load challenges or assign new ones
          setCurrentChallenge(null);
          setAssignedChallenges([]);
          choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
          setChoiceOrder([]);
          // Set time left to 0 since round is done
          setTimeLeft(0);
          // Don't proceed with challenge assignment or loading
          return;
        }
        
        // STEP 1: Check if user has assigned challenges, if not, assign them
        if (!roundData.assignedChallenges || roundData.assignedChallenges.length === 0) {
          // User hasn't been assigned challenges yet - assign them now
          await assignChallengesToUser(user, compData, activeRoundIndex, activeRound);
          
          // Reload user data after assignment
          const updatedUserDoc = await getDoc(doc(db, 'competitions', competitionId, 'users', userId));
          if (updatedUserDoc.exists()) {
            const updatedUser = { id: userId, ...updatedUserDoc.data() };
            setUserData(updatedUser);
            // Check again if round is done after assignment
            const updatedRoundData = updatedUser.roundData?.[activeRoundIndex] || {};
            if (!updatedRoundData.isDone) {
              await loadChallengesForUser(updatedUser, compData, activeRoundIndex);
            } else {
              setCurrentChallenge(null);
              setAssignedChallenges([]);
              choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
              setChoiceOrder([]);
              setTimeLeft(0);
            }
          }
        } else {
          // User already has assigned challenges - check if round is done before loading
          // Double-check isDone status from fresh user data
          const freshUserDoc = await getDoc(doc(db, 'competitions', competitionId, 'users', userId));
          if (freshUserDoc.exists()) {
            const freshUser = { id: userId, ...freshUserDoc.data() };
            const freshRoundData = freshUser.roundData?.[activeRoundIndex] || {};
            if (freshRoundData.isDone) {
              setCurrentChallenge(null);
              setAssignedChallenges([]);
              choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
              setChoiceOrder([]);
              setTimeLeft(0);
              setUserData(freshUser);
            } else {
              // If user has challenges but no startTime, do NOT set it here – wait for user to click Start
              if (!freshRoundData.startTime && activeRound.isActive) {
                setUserData(freshUser);
                // Do not call loadChallengesForUser; user will see start modal and timer won't run
              } else {
                await loadChallengesForUser(freshUser, compData, activeRoundIndex);
              }
            }
          }
        }
        
        // Calculate time left
        const currentRoundData = user.roundData?.[activeRoundIndex] || roundData;
        if (currentRoundData.startTime) {
          const startTime = currentRoundData.startTime.toDate ? currentRoundData.startTime.toDate() : new Date(currentRoundData.startTime);
          const currentTime = new Date();
          const elapsed = (currentTime - startTime) / 1000;
          const maxTime = activeRound.maxTime || 0;
          const remaining = Math.max(0, maxTime - elapsed);
          setTimeLeft(Math.floor(remaining));
        } else {
          setTimeLeft(activeRound.maxTime || 0);
        }
      } else {
        setCurrentChallenge(null);
        choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
        setChoiceOrder([]);
        setTimeLeft(0);
      }
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const assignChallengesToUser = async (user, competition, activeRoundIndex, activeRound) => {
    try {
      const userId = localStorage.getItem('userId');
      const competitionId = localStorage.getItem('competitionId');
      
      // STEP 2: Check round type (Fixed or Open)
      const roundType = activeRound.type; // 'fixed' or 'open'
      
      // STEP 3: Get all challenges matching the round's categories
      const challengesRef = collection(db, 'competitions', competitionId, 'challenges');
      const q = query(
        challengesRef,
        where('category', 'in', activeRound.categories || [])
      );
      const challengesSnapshot = await getDocs(q);
      const allMatchingChallenges = challengesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // STEP 4: Assign challenges based on round type
      let assignedChallengeIds = [];
      
      const count = roundType === 'fixed'
        ? (activeRound.numberOfChallenges || 0)
        : allMatchingChallenges.length;
      assignedChallengeIds = pickAssignedChallengeIds(
        allMatchingChallenges,
        count,
        shuffleQuestionsEnabled(activeRound)
      );
      
      // Initialize challenge states (all start as 'notsolved')
      const challengeStates = {};
      assignedChallengeIds.forEach(id => {
        challengeStates[id] = 'notsolved';
      });
      
      // Do not set startTime here – user must click Start in the modal
      const roundData = user.roundData || {};
      const currentRoundData = roundData[activeRoundIndex] || {};
      const startTime = currentRoundData.startTime || null;
      
      // Preserve isDone value - only set to false if it's not already true
      // This ensures completed rounds remain completed unless admin resets them
      const preserveIsDone = currentRoundData.isDone === true ? true : false;
      
      const updatedRoundData = {
        ...roundData,
        [activeRoundIndex]: {
          ...currentRoundData,
          startTime: startTime,
          score: currentRoundData.score || 0,
          correctAnswers: currentRoundData.correctAnswers || 0,
          assignedChallenges: assignedChallengeIds,
          challengeStates: challengeStates,
          isDone: preserveIsDone // Preserve existing isDone value if true, otherwise false
        }
      };
      
      await updateDoc(doc(db, 'competitions', competitionId, 'users', userId), {
        roundData: updatedRoundData
      });
    } catch (err) {
      console.error('Error assigning challenges:', err);
    }
  };

  const handleStartRound = async () => {
    const userId = localStorage.getItem('userId');
    const competitionId = localStorage.getItem('competitionId');
    if (!userId || !competitionId || !userData || !competition) return;
    const activeRoundIndex = competition.rounds?.findIndex(r => r.isActive);
    if (activeRoundIndex < 0) return;
    const roundData = userData.roundData || {};
    const currentRoundData = roundData[activeRoundIndex] || {};
    if (currentRoundData.startTime) return;
    try {
      const updatedRoundData = {
        ...userData.roundData,
        [activeRoundIndex]: {
          ...currentRoundData,
          startTime: Timestamp.now()
        }
      };
      await updateDoc(doc(db, 'competitions', competitionId, 'users', userId), {
        roundData: updatedRoundData
      });
      const updatedUserDoc = await getDoc(doc(db, 'competitions', competitionId, 'users', userId));
      if (updatedUserDoc.exists()) {
        const updatedUser = { id: userId, ...updatedUserDoc.data() };
        setUserData(updatedUser);
        await loadChallengesForUser(updatedUser, competition, activeRoundIndex);
      }
    } catch (err) {
      console.error('Error starting round:', err);
    }
  };

  const loadChallengesForUser = async (user, competition, activeRoundIndex) => {
    try {
      const roundData = user.roundData?.[activeRoundIndex] || {};
      
      // Check if round is already done
      if (roundData.isDone) {
        setAssignedChallenges([]);
        setCurrentChallenge(null);
        choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
        setChoiceOrder([]);
        return;
      }
      
      const assignedChallengeIds = roundData.assignedChallenges || [];
      const challengeStates = roundData.challengeStates || {};
      
      if (assignedChallengeIds.length === 0) {
        setAssignedChallenges([]);
        setCurrentChallenge(null);
        choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
        setChoiceOrder([]);
        return;
      }
      
      // Load all assigned challenges from Firestore
      const challenges = await Promise.all(
        assignedChallengeIds.map(async (challengeId) => {
          const challengeDoc = await getDoc(doc(db, 'competitions', competition.id, 'challenges', challengeId));
          if (challengeDoc.exists()) {
            return {
              ...challengeDoc.data(),
              id: challengeDoc.id,
              state: challengeStates[challengeId] || 'notsolved'
            };
          }
          return null;
        })
      );
      
      const validChallenges = challenges.filter(ch => ch !== null);
      setAssignedChallenges(validChallenges);
      
      // STEP 5: Find the next challenge to show (sequential - one after another)
      const nextChallengeIndex = validChallenges.findIndex(ch => 
        ch.state === 'notsolved' || !ch.state
      );
      
      if (nextChallengeIndex >= 0) {
        const ch = validChallenges[nextChallengeIndex];
        const choices = ch?.choices || [];
        const n = choices.length;
        const round = competition.rounds?.[activeRoundIndex] || {};
        const order = shuffleChoicesEnabled(round)
          ? shuffledChoiceOrder(n)
          : identityChoiceOrder(n);
        choiceOrderForChallengeRef.current = { challengeId: ch.id, order };
        setCurrentChallenge(ch);
        setCurrentChallengeIndex(nextChallengeIndex);
        setSelectedAnswers([]);
        setChoiceOrder(order);
        // Record when this challenge was loaded (start time for this challenge)
        setChallengeStartTime(new Date());
      } else {
        // All challenges are solved, mark round as done
        setCurrentChallenge(null);
        choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
        setChoiceOrder([]);
        if (!roundData.isDone) {
          await markRoundAsDone(activeRoundIndex);
        }
      }
    } catch (err) {
      console.error('Error loading challenges:', err);
    }
  };

  const setupRealtimeUpdates = () => {
    const userId = localStorage.getItem('userId');
    const competitionId = localStorage.getItem('competitionId');

    if (!userId || !competitionId) return null;

    // Listen to user updates
    const userRef = doc(db, 'competitions', competitionId, 'users', userId);
    const unsubscribe = onSnapshot(userRef, async (snapshot) => {
      if (snapshot.exists()) {
        const user = { id: snapshot.id, ...snapshot.data() };
        
        // During break after submit (including last question), don't apply realtime updates so user sees feedback + countdown
        if (breakParamsRef.current) {
          return;
        }
        
        // Check if round was reset by admin
        if (competition && previousRoundDataRef.current) {
          const activeRoundIndex = competition.rounds?.findIndex(r => r.isActive);
          if (activeRoundIndex >= 0) {
            const previousRoundData = previousRoundDataRef.current[activeRoundIndex];
            const currentRoundData = user.roundData?.[activeRoundIndex] || {};
            
            // Detect round reset by checking multiple indicators:
            // 1. isDone changed from true to false
            // 2. assignedChallenges was cleared (had challenges, now empty)
            // 3. score was reset to 0 (had score > 0, now 0)
            // 4. startTime was cleared (had startTime, now null)
            const wasDone = previousRoundData?.isDone === true;
            const isNowNotDone = currentRoundData.isDone === false;
            const hadChallenges = previousRoundData?.assignedChallenges?.length > 0;
            const hasNoChallenges = !currentRoundData.assignedChallenges || currentRoundData.assignedChallenges.length === 0;
            const hadScore = (previousRoundData?.score || 0) > 0;
            const hasNoScore = (currentRoundData.score || 0) === 0;
            const hadStartTime = previousRoundData?.startTime != null;
            const hasNoStartTime = currentRoundData.startTime == null;
            
            // If round was reset (multiple indicators suggest reset)
            const isReset = (wasDone && isNowNotDone) || 
                           (hadChallenges && hasNoChallenges && !currentRoundData.isDone) ||
                           (hadScore && hasNoScore && hadStartTime && hasNoStartTime);
            
            if (isReset) {
              console.log('Round reset detected, refreshing page...');
              // Reload all data to refresh the page
              await loadData();
              // Update the ref with new data
              previousRoundDataRef.current = user.roundData || {};
              return;
            }
          }
        }
        
        // Update previous round data ref
        previousRoundDataRef.current = user.roundData || {};
        setUserData(user);
        
        if (user.frozen) {
          navigate('/');
          return;
        }

        // Reload challenges if competition is available
        if (competition) {
          const activeRoundIndex = competition.rounds?.findIndex(r => r.isActive);
          if (activeRoundIndex >= 0) {
            const roundData = user.roundData?.[activeRoundIndex] || {};
            // Only load challenges if round is not done
            if (!roundData.isDone) {
              await loadChallengesForUser(user, competition, activeRoundIndex);
            } else {
              // Round is done - clear challenges
              setCurrentChallenge(null);
              setAssignedChallenges([]);
              choiceOrderForChallengeRef.current = { challengeId: null, order: [] };
              setChoiceOrder([]);
              setTimeLeft(0);
            }
          }
        }
      }
    });

    return unsubscribe;
  };

  const setupCompetitionListener = (competitionId) => {
    if (!competitionId) return null;

    // Listen to competition updates to detect when a new round starts
    const competitionRef = doc(db, 'competitions', competitionId);
    const unsubscribe = onSnapshot(competitionRef, async (snapshot) => {
      if (snapshot.exists()) {
        const compData = { id: snapshot.id, ...snapshot.data() };
        
        // Find the currently active round
        const currentActiveRoundIndex = compData.rounds?.findIndex(r => r.isActive);
        const previousActiveRoundIndex = previousActiveRoundIndexRef.current;
        
        // Check if a new round has started (different from previous active round)
        if (currentActiveRoundIndex >= 0 && currentActiveRoundIndex !== previousActiveRoundIndex) {
          // A new round has started - refresh the page by reloading all data
          console.log('New round started, refreshing page...');
          previousActiveRoundIndexRef.current = currentActiveRoundIndex;
          
          // Reload all data to get the new round and assign challenges
          await loadData();
        } else if (currentActiveRoundIndex >= 0) {
          // Update competition data but no new round
          setCompetition(compData);
          previousActiveRoundIndexRef.current = currentActiveRoundIndex;
        } else {
          // No active round
          setCompetition(compData);
          previousActiveRoundIndexRef.current = null;
        }
      }
    });

    return unsubscribe;
  };

  const markRoundAsDone = async (roundIndex) => {
    try {
      const userId = localStorage.getItem('userId');
      const competitionId = localStorage.getItem('competitionId');
      
      const roundData = userData.roundData || {};
      const currentRoundData = roundData[roundIndex] || {};
      
      const updatedRoundData = {
        ...roundData,
        [roundIndex]: {
          ...currentRoundData,
          isDone: true,
          endTime: new Date()
        }
      };

      await updateDoc(doc(db, 'competitions', competitionId, 'users', userId), {
        roundData: updatedRoundData
      });
      
      setUserData({ ...userData, roundData: updatedRoundData });
    } catch (err) {
      console.error('Error marking round as done:', err);
    }
  };

  const handleAnswerChange = (choiceIndex, isMultiple) => {
    // STEP 6: Handle answer selection based on challenge type
    if (isMultiple) {
      setSelectedAnswers(prev => 
        prev.includes(choiceIndex)
          ? prev.filter(i => i !== choiceIndex)
          : [...prev, choiceIndex]
      );
    } else {
      setSelectedAnswers([choiceIndex]);
    }
  };

  const handleSubmit = async () => {
    if (!currentChallenge || selectedAnswers.length === 0) return;
    
    // STEP 7: Disable input if time is 00:00:00
    if (timeLeft <= 0) {
      return;
    }

    setSubmitting(true);
    setFeedback(null);
    setFeedbackDetail(null);

    try {
      const userId = localStorage.getItem('userId');
      const competitionId = localStorage.getItem('competitionId');
      const user = userData;

      // Check if answer is correct
      const correctAnswers = currentChallenge.correctAnswers || [];
      const isCorrect = 
        selectedAnswers.length === correctAnswers.length &&
        selectedAnswers.every(ans => correctAnswers.includes(ans));

      // Get active round
      const activeRoundIndex = competition.rounds?.findIndex(r => r.isActive);
      if (activeRoundIndex < 0) {
        setFeedback('error');
        setSubmitting(false);
        return;
      }

      const round = competition.rounds[activeRoundIndex];
      const roundData = user.roundData || {};
      const currentRoundData = roundData[activeRoundIndex] || {};
      
      // Don't allow submission if round is done
      if (currentRoundData.isDone) {
        setFeedback('error');
        setSubmitting(false);
        return;
      }

      // Use round's correctValue and wrongValue
      const scoreChange = isCorrect 
        ? (round.correctValue || 0)
        : (round.wrongValue || 0);

      // Calculate time taken for this challenge (from when challenge was loaded to submission)
      // This excludes the time taken by system to switch between challenges
      const challengeEndTime = new Date();
      const timeTakenForChallenge = challengeStartTime 
        ? Math.max(0, Math.floor((challengeEndTime - challengeStartTime) / 1000)) // Time in seconds
        : 0;

      // Update total time used to submit ALL challenges (both correct and incorrect) for THIS ROUND
      // Store per-round instead of globally
      const currentRoundTotalTimeUsedToSubmit = currentRoundData.totalTimeUsedToSubmit || 0;
      const newRoundTotalTimeUsedToSubmit = currentRoundTotalTimeUsedToSubmit + timeTakenForChallenge;
      
      // Update correct answers count for THIS ROUND
      const currentRoundCorrectAnswersCount = currentRoundData.correctAnswersCount || 0;
      const newRoundCorrectAnswersCount = currentRoundCorrectAnswersCount + (isCorrect ? 1 : 0);

      // Update per-round data
      const roundScore = (currentRoundData.score || 0) + scoreChange;
      const roundCorrectAnswers = (currentRoundData.correctAnswers || 0) + (isCorrect ? 1 : 0);
      
      // Update challenge state
      const challengeStates = currentRoundData.challengeStates || {};
      challengeStates[currentChallenge.id] = isCorrect ? 'correct' : 'false';
      
      // Check if all challenges are solved
      const assignedChallenges = currentRoundData.assignedChallenges || [];
      const allSolved = assignedChallenges.every(id => challengeStates[id] === 'correct' || challengeStates[id] === 'false');
      const isRoundComplete = allSolved;
      
      // Prepare round data update - only include endTime if it exists
      // Preserve existing isDone value if it's already true (don't overwrite completed rounds)
      const preserveIsDone = currentRoundData.isDone === true ? true : (isRoundComplete || false);
      
      const roundDataUpdate = {
        ...currentRoundData,
        score: roundScore,
        correctAnswers: roundCorrectAnswers,
        challengeStates: challengeStates,
        isDone: preserveIsDone, // Preserve existing isDone if true, otherwise set based on completion
        totalTimeUsedToSubmit: newRoundTotalTimeUsedToSubmit, // Store per-round time for ALL challenges
        correctAnswersCount: newRoundCorrectAnswersCount // Store per-round count of correct answers
      };
      
      // Only add endTime if round is complete or if it already exists
      if (isRoundComplete) {
        roundDataUpdate.endTime = new Date();
      } else if (currentRoundData.endTime) {
        roundDataUpdate.endTime = currentRoundData.endTime;
      }
      
      const updatedRoundData = {
        ...roundData,
        [activeRoundIndex]: roundDataUpdate
      };

      // Calculate total score from all rounds
      const totalScore = Object.values(updatedRoundData).reduce((sum, rd) => sum + (rd.score || 0), 0);

      // Prepare answer data
      const answerData = {
        challengeId: currentChallenge.id,
        challenge: {
          id: currentChallenge.id,
          text: currentChallenge.text || currentChallenge.question, // Support both old and new property names
          choices: currentChallenge.choices,
          type: currentChallenge.type,
          category: currentChallenge.category
        },
        round: activeRoundIndex,
        selectedAnswers: selectedAnswers,
        correctAnswers: correctAnswers,
        isCorrect: isCorrect,
        submittedAt: challengeEndTime,
        challengeStartTime: challengeStartTime, // When challenge was loaded
        challengeEndTime: challengeEndTime, // When answer was submitted
        timeUsed: timeTakenForChallenge // Time taken to answer this challenge (in seconds)
      };

      // Prepare answer data with Timestamp for date fields
      const answerDataWithTimestamp = {
        ...answerData,
        submittedAt: answerData.submittedAt instanceof Date 
          ? Timestamp.fromDate(answerData.submittedAt)
          : answerData.submittedAt,
        challengeStartTime: answerData.challengeStartTime instanceof Date
          ? Timestamp.fromDate(answerData.challengeStartTime)
          : answerData.challengeStartTime,
        challengeEndTime: answerData.challengeEndTime instanceof Date
          ? Timestamp.fromDate(answerData.challengeEndTime)
          : answerData.challengeEndTime
      };
      
      // Prepare roundData with proper Timestamp conversion
      const roundDataForUpdate = {};
      Object.keys(updatedRoundData).forEach(key => {
        const roundDataItem = { ...updatedRoundData[key] };
        
        // Convert startTime if it's a Date (keep existing Timestamp if already converted)
        if (roundDataItem.startTime instanceof Date) {
          roundDataItem.startTime = Timestamp.fromDate(roundDataItem.startTime);
        }
        
        // Convert endTime if it's a Date (keep existing Timestamp if already converted)
        // Only include endTime if it exists (not undefined)
        if (roundDataItem.endTime) {
          if (roundDataItem.endTime instanceof Date) {
            roundDataItem.endTime = Timestamp.fromDate(roundDataItem.endTime);
          }
          // If it's already a Timestamp, keep it as is
        } else {
          // Remove endTime if it's undefined
          delete roundDataItem.endTime;
        }
        
        roundDataForUpdate[key] = roundDataItem;
      });
      
      // Update user in Firestore
      await updateDoc(doc(db, 'competitions', competitionId, 'users', userId), {
        score: totalScore,
        roundData: roundDataForUpdate,
        answers: [...(user.answers || []), answerDataWithTimestamp]
        // Note: totalTimeUsedToSubmit and correctAnswersCount are now stored per-round in roundData
      });
      
      const updatedUser = { 
        ...user, 
        roundData: updatedRoundData, 
        score: totalScore
      };
      // Update local user data (skip when round just completed so we keep showing the last question + feedback for 2s)
      if (!isRoundComplete) {
        setUserData(updatedUser);
      }
      
      // Reset challenge start time for next challenge
      setChallengeStartTime(null);

      setFeedback(isCorrect ? 'correct' : 'incorrect');
      setFeedbackDetail({
        isCorrect,
        correctIndices: correctAnswers,
        userSelected: [...selectedAnswers]
      });
      
      // Start break (countdown + skip); break time is not counted in round time. If length is 0, advance immediately.
      breakParamsRef.current = { activeRoundIndex, user, updatedRoundData, competition };
      breakStartTimeRef.current = Date.now();
      inBreakRef.current = true;
      if (breakLengthFromSettings <= 0) {
        advanceAfterBreak();
      } else {
        setBreakSecondsRemaining(breakLengthFromSettings);
      }
    } catch (err) {
      console.error('Error submitting answer:', err);
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        stack: err.stack
      });
      setFeedback('error');
      setFeedbackDetail(null);
      // Show alert with error details for debugging
      alert(`Error submitting answer: ${err.message || 'Unknown error'}. Check console for more details.`);
    } finally {
      setSubmitting(false);
    }
  };

  const advanceAfterBreak = async () => {
    const params = breakParamsRef.current;
    if (!params) return;
    const { activeRoundIndex, user: userFromBreak, updatedRoundData: updatedRoundDataFromBreak, competition: competitionFromBreak } = params;
    breakParamsRef.current = null;
    const breakEnd = Date.now();
    const breakDuration = (breakEnd - (breakStartTimeRef.current || breakEnd)) / 1000;
    totalBreakTimeAddedRef.current = (totalBreakTimeAddedRef.current || 0) + breakDuration;
    inBreakRef.current = false;
    setBreakSecondsRemaining(null);
    setFeedback(null);
    setFeedbackDetail(null);
    setSelectedAnswers([]);
    try {
      const userId = localStorage.getItem('userId');
      const competitionId = localStorage.getItem('competitionId');
      const userDoc = await getDoc(doc(db, 'competitions', competitionId, 'users', userId));
      if (userDoc.exists()) {
        const refreshedUser = { id: userId, ...userDoc.data() };
        const persistedRoundData = refreshedUser.roundData || {};
        const persistedCurrent = persistedRoundData[activeRoundIndex] || {};
        const totalBreakSeconds = totalBreakTimeAddedRef.current || 0;
        if ((persistedCurrent.totalBreakSeconds || 0) !== totalBreakSeconds) {
          const roundDataWithBreak = {
            ...persistedRoundData,
            [activeRoundIndex]: {
              ...persistedCurrent,
              totalBreakSeconds
            }
          };
          await updateDoc(doc(db, 'competitions', competitionId, 'users', userId), {
            roundData: roundDataWithBreak
          });
          refreshedUser.roundData = roundDataWithBreak;
        }
        setUserData(refreshedUser);
        const competitionDoc = await getDoc(doc(db, 'competitions', competitionId));
        if (competitionDoc.exists()) {
          const refreshedCompetition = { id: competitionId, ...competitionDoc.data() };
          setCompetition(refreshedCompetition);
          await loadChallengesForUser(refreshedUser, refreshedCompetition, activeRoundIndex);
        }
      }
    } catch (err) {
      console.error('Error refreshing after submission:', err);
      const updatedUser = {
        ...userFromBreak,
        roundData: updatedRoundDataFromBreak,
        score: userFromBreak.score
      };
      setUserData(updatedUser);
      await loadChallengesForUser(updatedUser, competitionFromBreak, activeRoundIndex);
    }
  };

  // Countdown during break between questions (3, 2, 1 then advance)
  useEffect(() => {
    if (breakSecondsRemaining == null || breakSecondsRemaining <= 0) return;
    const id = setInterval(() => {
      setBreakSecondsRemaining(prev => {
        if (prev == null || prev <= 1) {
          setTimeout(() => advanceAfterBreak(), 0);
          return null;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
    // advanceAfterBreak intentionally omitted: we only want to re-run when breakSecondsRemaining changes
  }, [breakSecondsRemaining]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateAverageTime = () => {
    // Calculate average time per successful challenge from completed rounds
    // Sum of totalTimeUsedToSubmit for all rounds where isDone: true
    // Divided by sum of correctAnswersCount for all rounds where isDone: true
    if (!userData || !userData.roundData) return 0;
    
    let totalTimeUsedToSubmit = 0;
    let totalCorrectAnswersCount = 0;
    
    // Sum values from all completed rounds
    Object.values(userData.roundData || {}).forEach(roundData => {
      if (roundData.isDone === true) {
        totalTimeUsedToSubmit += (roundData.totalTimeUsedToSubmit || 0);
        totalCorrectAnswersCount += (roundData.correctAnswersCount || 0);
      }
    });
    
    if (totalCorrectAnswersCount === 0) return 0;
    return totalTimeUsedToSubmit / totalCorrectAnswersCount;
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentRunningRound = () => {
    if (!competition?.rounds) return 0;
    const activeRoundIndex = competition.rounds.findIndex(r => r.isActive);
    return activeRoundIndex >= 0 ? activeRoundIndex + 1 : 0;
  };

  if (loading) {
    return (
      <div className="user-page">
        <Header inCompetition={true} onLeaveCompetition={() => {}} />
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  if (!userData || !competition) {
    return null;
  }

  const isTimeUp = timeLeft <= 0;
  const averageTime = calculateAverageTime();

  return (
    <div className="user-page">
      <Header 
        inCompetition={true} 
        onLeaveCompetition={async () => {
          localStorage.removeItem('userId');
          localStorage.removeItem('competitionId');
          localStorage.removeItem('userName');
          localStorage.removeItem('userDateOfBirth');
        }}
      />
      
      <div className="user-container">
        <div className="user-info">
          <h1>{competition.name}</h1>
          {(competition.startTime || competition.endTime) && (
            <p className="user-page-period">
              {t('competitionPeriod')}: {formatTimestamp(competition.startTime) || t('manualControl')}
              {' – '}
              {formatTimestamp(competition.endTime) || t('manualControl')}
            </p>
          )}
          {(() => {
            const activeRoundIndex = competition?.rounds?.findIndex(r => r.isActive);
            const round = activeRoundIndex >= 0 ? competition.rounds[activeRoundIndex] : null;
            const roundStart = round && formatTimestamp(round.startTime);
            const roundEnd = round && formatTimestamp(round.endTime);
            if (!roundStart && !roundEnd) return null;
            return (
              <p className="user-page-period">
                {t('roundPeriod')} ({t('round')} {activeRoundIndex + 1}): {roundStart || t('manualControl')}
                {' – '}
                {roundEnd || t('manualControl')}
              </p>
            );
          })()}
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">{t('name')}:</span>
              <span className="info-value">{userData.name}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('dateOfBirth')}:</span>
              <span className="info-value">{userData.dateOfBirth}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('currentRound')}:</span>
              <span className="info-value">{getCurrentRunningRound()}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('timeLeft')}:</span>
              <span className="info-value time-left">{formatTime(timeLeft || 0)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('currentScore')}:</span>
              <span className="info-value score">{userData.score}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('averageTimePerSuccessfulChallenge')}:</span>
              <span className="info-value">{formatTime(averageTime)}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('gender')}:</span>
              <span className="info-value">{userData.gender ? t(userData.gender) : '-'}</span>
            </div>
          </div>
        </div>

        {/* Start round modal: show when user has assigned challenges but has not clicked Start yet */}
        {(() => {
          const activeRoundIndex = competition?.rounds?.findIndex(r => r.isActive);
          if (activeRoundIndex < 0) return null;
          const roundData = userData?.roundData?.[activeRoundIndex] || {};
          const hasChallenges = (roundData.assignedChallenges?.length || 0) > 0;
          const hasNotStarted = !roundData.startTime;
          if (!hasChallenges || !hasNotStarted) return null;
          const instructions = roundStartInstructions[currentLanguage] || t('roundStartInstructionsDefault');
          return (
            <div className="round-start-overlay">
              <div className="round-start-modal">
                <h2 className="round-start-title">{t('roundStartTitle')}</h2>
                <div className="round-start-instructions">{instructions}</div>
                <button type="button" className="round-start-button" onClick={handleStartRound}>
                  {t('roundStartButton')}
                </button>
              </div>
            </div>
          );
        })()}

        {/* Read-only review of submitted answers for a completed round */}
        {reviewRoundIndex !== null && (() => {
          const roundAnswers = (userData?.answers || []).filter(
            a => Number(a.round) === reviewRoundIndex
          );
          return (
            <div className="review-answers-section">
              <div className="review-answers-header">
                <h2 className="review-answers-title">
                  {t('reviewRoundTitle').replace('{round}', String(reviewRoundIndex + 1))}
                </h2>
                <button
                  type="button"
                  className="review-answers-back"
                  onClick={() => setReviewRoundIndex(null)}
                >
                  {t('backToSummary')}
                </button>
              </div>
              <div className="review-answers-list">
                {roundAnswers.map((answer, idx) => {
                  const challenge = answer.challenge || {};
                  const questionText = challenge.text || challenge.question;
                  const choices = challenge.choices || [];
                  const selectedIndices = Array.isArray(answer.selectedAnswers) ? answer.selectedAnswers : [];
                  const correctIndices = Array.isArray(answer.correctAnswers) ? answer.correctAnswers : [];
                  const questionDisplay = typeof questionText === 'object'
                    ? (questionText[currentLanguage] || Object.values(questionText).find(q => q?.trim()) || t('noChallengeAvailable'))
                    : (questionText || t('noChallengeAvailable'));
                  return (
                    <div key={answer.challengeId || idx} className="review-answer-card">
                      <div className="review-answer-meta">
                        <span className="review-answer-number">{t('questionX').replace('{current}', String(idx + 1))}</span>
                        <span className={`review-answer-result ${answer.isCorrect ? 'correct' : 'incorrect'}`}>
                          {answer.isCorrect ? `✓ ${t('correct')}` : `✗ ${t('incorrect')}`}
                        </span>
                        {answer.timeUsed != null && (
                          <span className="review-answer-time">{t('timeUsed')}: {formatTime(Math.round(Number(answer.timeUsed)))}</span>
                        )}
                      </div>
                      <div className="review-answer-question">{questionDisplay}</div>
                      {(challenge.challengeImage || challenge.questionImage) && (
                        <div className="review-answer-image-wrap">
                          <img
                            src={challenge.challengeImage || challenge.questionImage}
                            alt=""
                            className="review-answer-image"
                          />
                        </div>
                      )}
                      <div className="review-answer-choices">
                        {choices.map((choice, cIdx) => {
                          const choiceText = typeof choice === 'object'
                            ? (choice[currentLanguage] || Object.values(choice).find(c => c?.trim()) || `Choice ${cIdx + 1}`)
                            : choice;
                          const choiceImage = typeof choice === 'object' && choice.image ? choice.image : null;
                          const isUserSelected = selectedIndices.includes(cIdx);
                          const isCorrectOption = correctIndices.includes(cIdx);
                          const wrongSelected = isUserSelected && !isCorrectOption;
                          let badge = null;
                          if (isUserSelected && isCorrectOption) badge = t('optionWasCorrect');
                          else if (isUserSelected && wrongSelected) badge = t('optionWasIncorrect');
                          else if (isCorrectOption && !isUserSelected) badge = t('correctAnswer');
                          return (
                            <div
                              key={cIdx}
                              className={`review-choice ${isUserSelected ? 'user-selected' : ''} ${isCorrectOption ? 'correct-option' : ''} ${wrongSelected ? 'wrong-selected' : ''}`}
                            >
                              <div className="review-choice-content">
                                {choiceImage && <img src={choiceImage} alt="" className="review-choice-img" />}
                                <span>{choiceText}</span>
                                {badge && <span className="review-choice-badge">{badge}</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Show message if round is already done (and not in break after last question) */}
        {reviewRoundIndex === null && (() => {
          const activeRoundIndex = competition?.rounds?.findIndex(r => r.isActive);
          const inBreakAfterSubmit = feedback && (breakSecondsRemaining != null);
          if (activeRoundIndex >= 0) {
            const roundData = userData?.roundData?.[activeRoundIndex] || {};
            // Show "Round Completed" only when round is done AND we're not in the break (so user saw feedback + countdown first)
            if (roundData.isDone && !inBreakAfterSubmit) {
              return (
                <div className="challenge-container" style={{ textAlign: 'center', padding: '2rem' }}>
                  <h2 style={{ color: 'var(--brand-primary)', marginBottom: '1rem' }}>{t('roundCompleted')}</h2>
                  <p style={{ fontSize: '1.1rem', color: '#666' }}>
                    {t('roundCompletedMessage').replace('{round}', activeRoundIndex + 1)}
                  </p>
                  <button
                    type="button"
                    className="review-my-answers-button"
                    onClick={() => setReviewRoundIndex(activeRoundIndex)}
                  >
                    {t('reviewMyAnswers')}
                  </button>
                </div>
              );
            }
          }
          return null;
        })()}

        {/* Show challenge if round not done, OR if we're in the break after the last question (so user sees feedback + countdown) */}
        {reviewRoundIndex === null && (() => {
          const activeRoundIndex = competition?.rounds?.findIndex(r => r.isActive);
          const inBreakAfterSubmit = feedback && (breakSecondsRemaining != null);
          if (activeRoundIndex >= 0) {
            const roundData = userData?.roundData?.[activeRoundIndex] || {};
            // If round is done and we're not in the break, don't show challenge (Round Completed is shown above)
            if (roundData.isDone && !inBreakAfterSubmit) {
              return null;
            }
          }
          
          // Only show challenge if round is not done and we have a current challenge.
          // Do not show "No more challenges" when user has challenges but hasn't clicked Start yet.
          const activeRoundIndexForNoChallenge = competition?.rounds?.findIndex(r => r.isActive);
          const roundDataForNoChallenge = activeRoundIndexForNoChallenge >= 0 ? (userData?.roundData?.[activeRoundIndexForNoChallenge] || {}) : {};
          const waitingForStart = (roundDataForNoChallenge.assignedChallenges?.length || 0) > 0 && !roundDataForNoChallenge.startTime;
          if (!currentChallenge && !waitingForStart) {
            return (
              <div className="no-challenge">
                <p>{t('noMoreChallenges')}</p>
              </div>
            );
          }
          if (!currentChallenge) {
            return null;
          }
          
          return (
            <div className="challenge-container">
              {(() => {
                const totalQuestions = assignedChallenges.length;
                const questionNumber = totalQuestions > 0 ? (currentChallengeIndex + 1) : 0;
                const correctCount = assignedChallenges.filter(c => c.state === 'correct').length + (feedback === 'correct' ? 1 : 0);
                const incorrectCount = assignedChallenges.filter(c => c.state === 'false').length + (feedback === 'incorrect' ? 1 : 0);
                return (
                  <div className="challenge-counter">
                    <span className="challenge-counter-question">
                      {t('questionX').replace('{current}', String(questionNumber))}
                    </span>
                    <span className="challenge-counter-stats">
                      <span className="challenge-counter-correct">{t('correctAnswers').replace('{count}', String(correctCount))}</span>
                      <span className="challenge-counter-incorrect">{t('incorrectAnswers').replace('{count}', String(incorrectCount))}</span>
                    </span>
                  </div>
                );
              })()}
              <h2>{t('currentChallenge')}</h2>
              <div className="challenge-question">
                {(currentChallenge.challengeImage || currentChallenge.questionImage) && (
                  <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
                    <img 
                      src={currentChallenge.challengeImage || currentChallenge.questionImage} 
                      alt="Challenge" 
                      style={{ maxWidth: '100%', maxHeight: '300px', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
                <p>
                  {typeof (currentChallenge.question || currentChallenge.text) === 'object' 
                    ? ((currentChallenge.question || currentChallenge.text)[currentLanguage] || 
                       Object.values(currentChallenge.question || currentChallenge.text).find(q => q?.trim()) || 
                       t('noChallengeAvailable'))
                    : (currentChallenge.question || currentChallenge.text)}
                </p>
              </div>

              <div className="challenge-choices">
                {(() => {
                  const choices = currentChallenge.choices || [];
                  const n = choices.length;
                  const activeRoundForChoices = competition?.rounds?.find((r) => r.isActive) || {};
                  const shuffleChoices = shuffleChoicesEnabled(activeRoundForChoices);
                  const ref = choiceOrderForChallengeRef.current;
                  let order =
                    ref.challengeId === currentChallenge.id && ref.order.length === n
                      ? ref.order
                      : choiceOrder.length === n
                        ? choiceOrder
                        : (() => {
                            const nextOrder = shuffleChoices
                              ? shuffledChoiceOrder(n)
                              : identityChoiceOrder(n);
                            choiceOrderForChallengeRef.current = { challengeId: currentChallenge.id, order: nextOrder };
                            return nextOrder;
                          })();
                  if (shuffleChoices && n > 1 && order.every((val, i) => val === i)) {
                    order = shuffledChoiceOrder(n);
                    choiceOrderForChallengeRef.current = { challengeId: currentChallenge.id, order };
                  }
                  if (!shuffleChoices) {
                    order = identityChoiceOrder(n);
                    choiceOrderForChallengeRef.current = { challengeId: currentChallenge.id, order };
                  }
                  return order.map((origIdx) => {
                  const choice = choices[origIdx];
                  if (choice == null) return null;
                  // Determine if multiple choice based on challenge type
                  const isMultiple = currentChallenge.type === 'multiple-choice' ||
                                   currentChallenge.type === 'text-image-multiple-choice';

                  // Get choice text in current language
                  const choiceText = typeof choice === 'object'
                    ? (choice[currentLanguage] ||
                       Object.values(choice).find(c => c?.trim()) ||
                       `${t('choice')} ${origIdx + 1}`)
                    : choice;
                  const choiceImage = typeof choice === 'object' && choice.image ? choice.image : null;

                  const showFeedback = feedbackDetail && (feedback === 'correct' || feedback === 'incorrect');
                  const isCorrectOption = showFeedback && feedbackDetail.correctIndices.includes(origIdx);
                  const wasSelected = showFeedback && feedbackDetail.userSelected.includes(origIdx);
                  const isWrongSelected = showFeedback && wasSelected && !isCorrectOption;

                  const choiceFeedbackClass = showFeedback
                    ? isCorrectOption
                      ? wasSelected
                        ? 'choice-feedback-correct-selected'
                        : 'choice-feedback-correct-missed'
                      : isWrongSelected
                        ? 'choice-feedback-wrong-selected'
                        : ''
                    : '';

                  return (
                    <label key={origIdx} className={`choice-label ${choiceFeedbackClass}`.trim()} style={{
                      opacity: isTimeUp ? 0.6 : 1,
                      pointerEvents: (submitting || feedback || isTimeUp) ? 'none' : 'auto'
                    }}>
                      <span className={`choice-input-wrap ${isMultiple ? 'choice-checkbox' : 'choice-radio'}`}>
                        <input
                          type={isMultiple ? 'checkbox' : 'radio'}
                          name="answer"
                          checked={selectedAnswers.includes(origIdx)}
                          onChange={() => handleAnswerChange(origIdx, isMultiple)}
                          disabled={submitting || feedback || isTimeUp}
                          aria-label={t('choice') + ' ' + (origIdx + 1)}
                        />
                        <span className="choice-box" aria-hidden="true" />
                      </span>
                      <div className="choice-content" style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 }}>
                        {choiceImage && (
                          <img
                            src={choiceImage}
                            alt={`Choice ${origIdx + 1}`}
                            style={{ maxWidth: '100%', maxHeight: '150px', borderRadius: '6px', border: '1px solid #e0e0e0' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        {(choiceText && choiceText.trim()) && <span>{choiceText}</span>}
                        {showFeedback && (
                          <span className="choice-feedback-badge" aria-hidden="true">
                            {isCorrectOption && wasSelected && <span className="badge-ok">✓ {t('optionWasCorrect')}</span>}
                            {isCorrectOption && !wasSelected && <span className="badge-missed">✓ {t('correctAnswer')}</span>}
                            {isWrongSelected && <span className="badge-wrong">✗ {t('optionWasIncorrect')}</span>}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                  });
                })()}
              </div>

              {feedback && (
                <div className={`feedback ${feedback}`}>
                  {feedback === 'correct' && <span>✓ {t('correct')}</span>}
                  {feedback === 'incorrect' && <span>✗ {t('incorrect')}</span>}
                  {feedback === 'error' && <span>{t('errorOccurredShort')}</span>}
                </div>
              )}

              {feedback && (feedback === 'correct' || feedback === 'incorrect') && (
                <div className="break-countdown">
                  {breakSecondsRemaining != null && breakSecondsRemaining > 0 ? (
                    <>
                      <p className="break-countdown-text">{t('nextQuestionIn').replace('{seconds}', String(breakSecondsRemaining))}</p>
                      <button type="button" className="skip-break-button" onClick={advanceAfterBreak}>
                        {t('skipBreak')}
                      </button>
                    </>
                  ) : null}
                </div>
              )}

              <button
                className="submit-challenge-button"
                onClick={handleSubmit}
                disabled={
                  selectedAnswers.length === 0 ||
                  submitting ||
                  !!feedback ||
                  (breakSecondsRemaining != null && breakSecondsRemaining > 0) ||
                  isTimeUp
                }
                style={{
                  opacity: submitting || feedback || (breakSecondsRemaining != null && breakSecondsRemaining > 0) || isTimeUp ? 0.6 : 1,
                  cursor: submitting || feedback || (breakSecondsRemaining != null && breakSecondsRemaining > 0) || isTimeUp ? 'not-allowed' : 'pointer'
                }}
              >
                {t('submit')}
              </button>
            </div>
          );
        })()}
      </div>
      <Footer />
    </div>
  );
};

export default UserPage;
